import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Admin sayfası için noindex meta tag ekle
const setNoIndexMeta = () => {
  // Mevcut robots meta tag'ı kontrol et veya oluştur
  let metaRobots = document.querySelector('meta[name="robots"]');
  if (!metaRobots) {
    metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    document.head.appendChild(metaRobots);
  }
  metaRobots.content = 'noindex, nofollow';
  
  // Googlebot için ayrı meta tag
  let metaGooglebot = document.querySelector('meta[name="googlebot"]');
  if (!metaGooglebot) {
    metaGooglebot = document.createElement('meta');
    metaGooglebot.name = 'googlebot';
    document.head.appendChild(metaGooglebot);
  }
  metaGooglebot.content = 'noindex, nofollow';
};

// Bildirim sesi çalma fonksiyonu
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // İki tonlu bildirim sesi
    const playTone = (frequency, startTime, duration) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioContext.currentTime;
    // Ding-dong sesi
    playTone(830, now, 0.2);        // Yüksek ton
    playTone(660, now + 0.2, 0.3);  // Düşük ton
    playTone(830, now + 0.5, 0.2);  // Tekrar yüksek
    playTone(660, now + 0.7, 0.4);  // Uzun düşük ton
    
  } catch (error) {
    console.log('Ses çalınamadı:', error);
  }
};

function Yonetim() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  
  // Ses bildirimi için state'ler
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [lastOrderCount, setLastOrderCount] = useState(null);
  const [newOrderAlert, setNewOrderAlert] = useState(false);
  const isFirstLoad = useRef(true);
  
  // Ürün modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category_id: '', unit: 'adet', sales_unit: 'adet', is_available: true
  });

  // Kategori modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });

  // Sipariş detay modal
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Sipariş tarih filtresi
  const [orderFilter, setOrderFilter] = useState('today'); // 'today', 'week', 'month', 'all'

  // Ürün arama ve kategori filtresi
  const [productSearch, setProductSearch] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');

  // Paket servis durumu
  const [deliveryStatus, setDeliveryStatus] = useState({ is_active: true, closed_reason: '' });
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [closedReason, setClosedReason] = useState('');

  // Yeni sipariş kontrolü
  const checkNewOrders = useCallback(async () => {
    if (!isLoggedIn) return;
    
    try {
      const response = await axios.get(`${API}/admin/orders`);
      const currentOrders = response.data;
      const newOrderCount = currentOrders.filter(o => o.status === 'new').length;
      
      // İlk yüklemede sadece sayıyı kaydet
      if (isFirstLoad.current) {
        setLastOrderCount(newOrderCount);
        isFirstLoad.current = false;
        setOrders(currentOrders);
        return;
      }
      
      // Yeni sipariş var mı kontrol et
      if (lastOrderCount !== null && newOrderCount > lastOrderCount) {
        // Yeni sipariş geldi!
        if (soundEnabled) {
          playNotificationSound();
        }
        setNewOrderAlert(true);
        setTimeout(() => setNewOrderAlert(false), 5000);
        
        // Tarayıcı bildirimi (izin varsa)
        if (Notification.permission === 'granted') {
          new Notification('🍞 Yeni Sipariş!', {
            body: `${newOrderCount - lastOrderCount} yeni sipariş geldi!`,
            icon: '🛒'
          });
        }
      }
      
      setLastOrderCount(newOrderCount);
      setOrders(currentOrders);
    } catch (err) {
      console.error('Sipariş kontrolü hatası:', err);
    }
  }, [isLoggedIn, lastOrderCount, soundEnabled]);

  // İlk yükleme
  useEffect(() => {
    // Admin sayfasını arama motorlarından gizle
    setNoIndexMeta();
    document.title = 'Yönetim Paneli';
    
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsLoggedIn(true);
      loadData();
      
      // Tarayıcı bildirim izni iste
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  }, []);
  
  // Periyodik sipariş kontrolü (her 15 saniye)
  useEffect(() => {
    if (!isLoggedIn) return;
    
    const interval = setInterval(() => {
      checkNewOrders();
    }, 15000); // 15 saniyede bir kontrol
    
    return () => clearInterval(interval);
  }, [isLoggedIn, checkNewOrders]);

  const loadData = async () => {
    await Promise.all([loadProducts(), loadCategories(), loadOrders(), loadUsers(), loadStats(), loadDeliveryStatus()]);
  };

  // Paket servis durumunu yükle
  const loadDeliveryStatus = async () => {
    try {
      const response = await axios.get(`${API}/delivery-info`);
      setDeliveryStatus({
        is_active: response.data.is_active ?? true,
        closed_reason: response.data.closed_reason || ''
      });
    } catch (err) { console.error('Teslimat durumu yüklenemedi:', err); }
  };

  // Paket servisi aç/kapat
  const toggleDeliveryStatus = async (isActive, reason = '') => {
    try {
      await axios.put(`${API}/delivery-info`, {
        is_active: isActive,
        closed_reason: isActive ? '' : reason
      });
      setDeliveryStatus({ is_active: isActive, closed_reason: isActive ? '' : reason });
      setShowDeliveryModal(false);
      setClosedReason('');
      alert(isActive ? '✅ Paket servis açıldı!' : '🔴 Paket servis kapatıldı!');
    } catch (err) { 
      alert('Durum güncellenemedi!'); 
      console.error(err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('adminToken', response.data.token);
      setIsLoggedIn(true);
      loadData();
    } catch (err) {
      setError('Kullanıcı adı veya şifre hatalı!');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setIsLoggedIn(false);
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/products`);
      setProducts(response.data);
    } catch (err) { console.error('Ürünler yüklenemedi:', err); }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/admin/categories`);
      setCategories(response.data);
    } catch (err) { console.error('Kategoriler yüklenemedi:', err); }
  };

  const loadOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`);
      setOrders(response.data);
    } catch (err) { console.error('Siparişler yüklenemedi:', err); }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (err) { console.error('Üyeler yüklenemedi:', err); }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (err) { console.error('İstatistikler yüklenemedi:', err); }
  };

  const toggleStock = async (productId, currentStatus) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/stock?is_available=${!currentStatus}`);
      loadProducts();
    } catch (err) { alert('Stok güncellenemedi!'); }
  };

  const deleteProduct = async (productId, productName) => {
    if (window.confirm(`"${productName}" ürününü silmek istediğinizden emin misiniz?`)) {
      try {
        await axios.delete(`${API}/admin/products/${productId}`);
        loadProducts();
      } catch (err) { alert('Ürün silinemedi!'); }
    }
  };

  const openProductModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name, description: product.description || '', price: product.price.toString(),
        category_id: product.category_id || '', unit: product.unit || 'adet', 
        sales_unit: product.sales_unit || 'adet', is_available: product.is_available
      });
    } else {
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', category_id: categories[0]?.id || '', unit: 'adet', sales_unit: 'adet', is_available: true });
    }
    setShowProductModal(true);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        name: productForm.name, description: productForm.description, price: parseFloat(productForm.price),
        category_id: productForm.category_id, unit: productForm.unit, sales_unit: productForm.sales_unit, is_available: productForm.is_available
      };
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data);
      } else {
        await axios.post(`${API}/admin/products`, data);
      }
      setShowProductModal(false);
      loadProducts();
    } catch (err) { alert('Ürün kaydedilemedi!'); }
    finally { setLoading(false); }
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/categories`, categoryForm);
      setShowCategoryModal(false);
      setCategoryForm({ name: '', description: '' });
      loadCategories();
    } catch (err) { alert('Kategori eklenemedi!'); }
    finally { setLoading(false); }
  };

  const deleteCategory = async (categoryId, categoryName) => {
    if (window.confirm(`"${categoryName}" kategorisini silmek istediğinizden emin misiniz?`)) {
      try {
        await axios.delete(`${API}/admin/categories/${categoryId}`);
        loadCategories();
      } catch (err) { alert(err.response?.data?.detail || 'Kategori silinemedi!'); }
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`);
      loadOrders();
      loadStats();
      if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder({...selectedOrder, status});
      }
    } catch (err) { alert('Durum güncellenemedi!'); }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'new': { text: 'Yeni', color: '#3b82f6', bg: '#dbeafe' },
      'preparing': { text: 'Hazırlanıyor', color: '#f59e0b', bg: '#fef3c7' },
      'on_the_way': { text: 'Yolda', color: '#8b5cf6', bg: '#ede9fe' },
      'delivered': { text: 'Teslim Edildi', color: '#22c55e', bg: '#dcfce7' },
      'cancelled': { text: 'İptal', color: '#ef4444', bg: '#fee2e2' }
    };
    const s = statusMap[status] || { text: status, color: '#6b7280', bg: '#f3f4f6' };
    return <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', color: s.color, backgroundColor: s.bg }}>{s.text}</span>;
  };

  const getPaymentMethod = (method) => {
    const methods = { 'cash': '💵 Kapıda Nakit', 'card_on_delivery': '💳 Kapıda Kart', 'online_iyzico': '🌐 Online' };
    return methods[method] || method;
  };

  const formatDate = (dateStr) => {
    // Format: "2026-01-11 20:14:46" veya "2026-01-11T20:14:46"
    if (!dateStr) return '';
    
    // T veya boşluk ile ayır
    const parts = dateStr.replace('T', ' ').split(' ');
    if (parts.length < 2) return dateStr;
    
    const datePart = parts[0]; // 2026-01-11
    const timePart = parts[1].substring(0, 5); // 20:14
    
    const [year, month, day] = datePart.split('-');
    return `${day}.${month}.${year} ${timePart}`;
  };

  // Siparişleri tarihe göre filtrele
  const getFilteredOrders = () => {
    if (!orders.length) return [];
    
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(todayStart);
    weekStart.setDate(weekStart.getDate() - 7);
    const monthStart = new Date(todayStart);
    monthStart.setMonth(monthStart.getMonth() - 1);

    return orders.filter(order => {
      const orderDate = new Date(order.created_at);
      switch (orderFilter) {
        case 'today':
          return orderDate >= todayStart;
        case 'week':
          return orderDate >= weekStart;
        case 'month':
          return orderDate >= monthStart;
        case 'all':
        default:
          return true;
      }
    });
  };

  const filteredOrders = getFilteredOrders();

  // Login ekranı
  if (!isLoggedIn) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6' }}>
        <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: '400px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#d97706', marginBottom: '30px', textAlign: 'center' }}>🏪 Mis Fırın Yönetim</h1>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Kullanıcı Adı</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }} required />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Şifre</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }} required />
            </div>
            {error && <div style={{ padding: '12px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '8px', marginBottom: '20px', textAlign: 'center' }}>{error}</div>}
            <button type="submit" disabled={loading}
              style={{ width: '100%', padding: '14px', backgroundColor: '#d97706', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>
          </form>
          <div style={{ marginTop: '20px', textAlign: 'center' }}><a href="/" style={{ color: '#d97706' }}>← Ana Sayfaya Dön</a></div>
        </div>
      </div>
    );
  }

  // Admin Panel
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Pulse animasyonu için CSS */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.02); }
        }
      `}</style>
      
      {/* Header */}
      <div style={{ backgroundColor: '#d97706', color: 'white', padding: '15px 20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>🏪 Mis Fırın Yönetim</h1>
          <button onClick={handleLogout} style={{ padding: '8px 16px', backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid white', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Çıkış</button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
          {/* Paket Servis Durumu */}
          <div style={{ 
            backgroundColor: deliveryStatus.is_active ? '#dcfce7' : '#fee2e2', 
            padding: '15px 20px', 
            borderRadius: '12px', 
            marginBottom: '15px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '24px' }}>{deliveryStatus.is_active ? '🟢' : '🔴'}</span>
              <div>
                <p style={{ margin: 0, fontWeight: 'bold', color: deliveryStatus.is_active ? '#166534' : '#991b1b' }}>
                  Paket Servis: {deliveryStatus.is_active ? 'AÇIK' : 'KAPALI'}
                </p>
                {!deliveryStatus.is_active && deliveryStatus.closed_reason && (
                  <p style={{ margin: '3px 0 0 0', fontSize: '13px', color: '#991b1b' }}>
                    Sebep: {deliveryStatus.closed_reason}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => {
                if (deliveryStatus.is_active) {
                  setShowDeliveryModal(true);
                } else {
                  toggleDeliveryStatus(true);
                }
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: deliveryStatus.is_active ? '#ef4444' : '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px'
              }}
            >
              {deliveryStatus.is_active ? '🔴 Paket Servisi Kapat' : '🟢 Paket Servisi Aç'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px' }}>
            <div style={{ backgroundColor: '#dbeafe', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#1e40af', margin: 0 }}>{stats.new_orders}</p>
              <p style={{ color: '#1e40af', margin: '5px 0 0 0' }}>Yeni Sipariş</p>
            </div>
            <div style={{ backgroundColor: '#dcfce7', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#166534', margin: 0 }}>{stats.today_orders}</p>
              <p style={{ color: '#166534', margin: '5px 0 0 0' }}>Bugün Sipariş</p>
            </div>
            <div style={{ backgroundColor: '#d1fae5', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#065f46', margin: 0 }}>{(stats.today_sales || 0).toFixed(0)}₺</p>
              <p style={{ color: '#065f46', margin: '5px 0 0 0' }}>Bugün Satış</p>
            </div>
            <div style={{ backgroundColor: '#fef3c7', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#92400e', margin: 0 }}>{stats.total_orders}</p>
              <p style={{ color: '#92400e', margin: '5px 0 0 0' }}>Toplam Sipariş</p>
            </div>
            <div style={{ backgroundColor: '#ede9fe', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#5b21b6', margin: 0 }}>{stats.total_users}</p>
              <p style={{ color: '#5b21b6', margin: '5px 0 0 0' }}>Üye</p>
            </div>
            <div style={{ backgroundColor: '#fff7ed', padding: '20px', borderRadius: '12px', textAlign: 'center' }}>
              <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#c2410c', margin: 0 }}>{stats.total_sales.toFixed(0)}₺</p>
              <p style={{ color: '#c2410c', margin: '5px 0 0 0' }}>Toplam Satış</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '0', overflowX: 'auto' }}>
          {[
            { key: 'orders', icon: '📦', label: 'Siparişler', count: orders.length },
            { key: 'products', icon: '🍞', label: 'Ürünler', count: products.length },
            { key: 'categories', icon: '📁', label: 'Kategoriler', count: categories.length },
            { key: 'users', icon: '👥', label: 'Üyeler', count: users.length }
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              style={{ padding: '15px 25px', backgroundColor: activeTab === tab.key ? '#fff7ed' : 'white', border: 'none', borderBottom: activeTab === tab.key ? '3px solid #d97706' : '3px solid transparent', color: activeTab === tab.key ? '#d97706' : '#6b7280', fontWeight: '600', cursor: 'pointer', fontSize: '15px', whiteSpace: 'nowrap' }}>
              {tab.icon} {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
        
        {/* SİPARİŞLER */}
        {activeTab === 'orders' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>📦 Siparişler</h2>
              
              {/* Yeni Sipariş Uyarısı */}
              {newOrderAlert && (
                <div style={{ 
                  backgroundColor: '#dcfce7', 
                  color: '#166534', 
                  padding: '10px 20px', 
                  borderRadius: '8px', 
                  fontWeight: 'bold',
                  animation: 'pulse 1s infinite',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  🔔 YENİ SİPARİŞ GELDİ!
                </div>
              )}
              
              {/* Ses Ayarı */}
              <button 
                onClick={() => {
                  setSoundEnabled(!soundEnabled);
                  if (!soundEnabled) playNotificationSound(); // Test sesi
                }}
                style={{ 
                  padding: '8px 16px', 
                  borderRadius: '8px', 
                  border: 'none', 
                  backgroundColor: soundEnabled ? '#dcfce7' : '#fee2e2',
                  color: soundEnabled ? '#166534' : '#991b1b',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                {soundEnabled ? '🔔 Ses Açık' : '🔕 Ses Kapalı'}
              </button>
            </div>

            {/* Tarih Filtresi */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', overflowX: 'auto', paddingBottom: '5px' }}>
              {[
                { key: 'today', label: '📅 Bugün', count: orders.filter(o => new Date(o.created_at) >= new Date(new Date().setHours(0,0,0,0))).length },
                { key: 'week', label: '📆 Son 7 Gün', count: null },
                { key: 'month', label: '🗓️ Son 1 Ay', count: null },
                { key: 'all', label: '📚 Tümü', count: orders.length }
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => setOrderFilter(filter.key)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    backgroundColor: orderFilter === filter.key ? '#d97706' : '#f3f4f6',
                    color: orderFilter === filter.key ? 'white' : '#374151',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {filter.label}
                  {filter.count !== null && (
                    <span style={{
                      backgroundColor: orderFilter === filter.key ? 'rgba(255,255,255,0.3)' : '#e5e7eb',
                      padding: '2px 8px',
                      borderRadius: '10px',
                      fontSize: '12px'
                    }}>
                      {filter.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            
            {filteredOrders.length === 0 ? (
              <div style={{ backgroundColor: 'white', padding: '60px', borderRadius: '12px', textAlign: 'center', color: '#6b7280' }}>
                <p style={{ fontSize: '48px', marginBottom: '15px' }}>
                  {orderFilter === 'today' ? '🌅' : '📦'}
                </p>
                <p style={{ fontSize: '16px', marginBottom: '10px' }}>
                  {orderFilter === 'today' 
                    ? 'Bugün henüz sipariş yok' 
                    : 'Bu dönemde sipariş bulunamadı'}
                </p>
                {orderFilter === 'today' && orders.length > 0 && (
                  <button
                    onClick={() => setOrderFilter('all')}
                    style={{
                      marginTop: '10px',
                      padding: '10px 20px',
                      backgroundColor: '#d97706',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Tüm Siparişleri Gör ({orders.length})
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {filteredOrders.map(order => (
                  <div key={order.id} onClick={() => setSelectedOrder(order)}
                    style={{ backgroundColor: 'white', padding: '16px 20px', borderRadius: '10px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: `4px solid ${order.status === 'new' ? '#3b82f6' : order.status === 'delivered' ? '#22c55e' : '#f59e0b'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '16px' }}>#{order.order_number}</span>
                          {getStatusBadge(order.status)}
                        </div>
                        <p style={{ margin: '0', color: '#6b7280', fontSize: '14px' }}>
                          {order.customer_name} • {order.customer_phone}
                        </p>
                        <p style={{ margin: '3px 0 0 0', color: '#9ca3af', fontSize: '13px' }}>{formatDate(order.created_at)}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: '0', fontWeight: 'bold', fontSize: '18px', color: '#d97706' }}>{order.total.toFixed(2)}₺</p>
                        <p style={{ margin: '3px 0 0 0', color: '#6b7280', fontSize: '13px' }}>{getPaymentMethod(order.payment_method)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ÜRÜNLER */}
        {activeTab === 'products' && (
          <div>
            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>🍞 Ürünler ({products.length})</h2>
              <button onClick={() => openProductModal()} style={{ padding: '12px 24px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>➕ Yeni Ürün</button>
            </div>
            
            {/* Arama ve Filtre */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
              {/* Arama */}
              <div style={{ flex: '1', minWidth: '200px' }}>
                <input
                  type="text"
                  placeholder="🔍 Ürün ara..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '10px',
                    fontSize: '15px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              {/* Kategori Filtresi */}
              <select
                value={productCategoryFilter}
                onChange={(e) => setProductCategoryFilter(e.target.value)}
                style={{
                  padding: '12px 16px',
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  fontSize: '15px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  minWidth: '180px'
                }}
              >
                <option value="all">📁 Tüm Kategoriler</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            {/* Kategori Sekmeleri */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', overflowX: 'auto', paddingBottom: '5px' }}>
              <button
                onClick={() => setProductCategoryFilter('all')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  backgroundColor: productCategoryFilter === 'all' ? '#d97706' : '#f3f4f6',
                  color: productCategoryFilter === 'all' ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '13px',
                  whiteSpace: 'nowrap'
                }}
              >
                Tümü ({products.length})
              </button>
              {categories.map(cat => {
                const count = products.filter(p => p.category_id === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setProductCategoryFilter(cat.id)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: 'none',
                      backgroundColor: productCategoryFilter === cat.id ? '#d97706' : '#f3f4f6',
                      color: productCategoryFilter === cat.id ? 'white' : '#374151',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '13px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {cat.name} ({count})
                  </button>
                );
              })}
            </div>

            {/* Ürün Listesi */}
            <div style={{ display: 'grid', gap: '12px' }}>
              {products
                .filter(product => {
                  // Kategori filtresi
                  if (productCategoryFilter !== 'all' && product.category_id !== productCategoryFilter) {
                    return false;
                  }
                  // Arama filtresi
                  if (productSearch && !product.name.toLowerCase().includes(productSearch.toLowerCase())) {
                    return false;
                  }
                  return true;
                })
                .map(product => (
                <div key={product.id} style={{ backgroundColor: 'white', padding: '16px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: product.is_available ? '4px solid #22c55e' : '4px solid #ef4444', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>{product.name}</h3>
                    <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>{product.category_name || 'Kategorisiz'}</p>
                    <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#d97706', margin: '5px 0 0 0' }}>
                      {product.price}₺{product.sales_unit === 'kg' ? '/kg' : ''}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button onClick={() => toggleStock(product.id, product.is_available)} style={{ padding: '8px 16px', backgroundColor: product.is_available ? '#22c55e' : '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                      {product.is_available ? '✅ Stokta' : '❌ Tükendi'}
                    </button>
                    <button onClick={() => openProductModal(product)} style={{ padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>✏️</button>
                    <button onClick={() => deleteProduct(product.id, product.name)} style={{ padding: '8px 16px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🗑️</button>
                  </div>
                </div>
              ))}
              
              {/* Sonuç bulunamadı */}
              {products.filter(p => {
                if (productCategoryFilter !== 'all' && p.category_id !== productCategoryFilter) return false;
                if (productSearch && !p.name.toLowerCase().includes(productSearch.toLowerCase())) return false;
                return true;
              }).length === 0 && (
                <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '12px', textAlign: 'center', color: '#6b7280' }}>
                  <p style={{ fontSize: '40px', marginBottom: '10px' }}>🔍</p>
                  <p>Ürün bulunamadı</p>
                  {productSearch && (
                    <button
                      onClick={() => setProductSearch('')}
                      style={{ marginTop: '10px', padding: '8px 16px', backgroundColor: '#d97706', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      Aramayı Temizle
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* KATEGORİLER */}
        {activeTab === 'categories' && (
          <div>
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>📁 Kategoriler</h2>
              <button onClick={() => setShowCategoryModal(true)} style={{ padding: '12px 24px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}>➕ Yeni Kategori</button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {categories.map(category => (
                <div key={category.id} style={{ backgroundColor: 'white', padding: '16px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <div>
                    <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>{category.name}</h3>
                    <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>{products.filter(p => p.category_id === category.id).length} ürün</p>
                  </div>
                  <button onClick={() => deleteCategory(category.id, category.name)} style={{ padding: '8px 16px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>🗑️</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ÜYELER */}
        {activeTab === 'users' && (
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>👥 Kayıtlı Üyeler</h2>
            {users.length === 0 ? (
              <div style={{ backgroundColor: 'white', padding: '60px', borderRadius: '12px', textAlign: 'center', color: '#6b7280' }}>
                <p style={{ fontSize: '48px', marginBottom: '15px' }}>👥</p>
                <p>Henüz kayıtlı üye yok</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '12px' }}>
                {users.map(user => (
                  <div key={user.id} style={{ backgroundColor: 'white', padding: '16px 20px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' }}>{user.name}</h3>
                        <p style={{ margin: '0', color: '#6b7280', fontSize: '14px' }}>{user.email} • {user.phone}</p>
                        {user.address_mahalle && (
                          <p style={{ margin: '5px 0 0 0', color: '#9ca3af', fontSize: '13px' }}>
                            📍 {user.address_mahalle}, {user.address_sokak} No:{user.address_bina_no}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: '0', color: '#9ca3af', fontSize: '13px' }}>Kayıt: {formatDate(user.created_at)}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sipariş Detay Modal */}
      {selectedOrder && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '600px', maxHeight: '95vh', overflow: 'auto' }}>
            {/* Header - Sticky */}
            <div style={{ position: 'sticky', top: 0, backgroundColor: 'white', padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 10 }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>#{selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)} style={{ background: '#f3f4f6', border: 'none', fontSize: '20px', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            
            <div style={{ padding: '16px' }}>
              {/* Durum Güncelle - Yatay Scroll */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>Sipariş Durumu:</label>
                <div style={{ display: 'flex', gap: '8px', overflowX: 'auto', paddingBottom: '8px', WebkitOverflowScrolling: 'touch' }}>
                  {['new', 'preparing', 'on_the_way', 'delivered', 'cancelled'].map(status => (
                    <button key={status} onClick={() => updateOrderStatus(selectedOrder.id, status)}
                      style={{ 
                        padding: '10px 14px', 
                        backgroundColor: selectedOrder.status === status ? '#d97706' : '#f3f4f6', 
                        color: selectedOrder.status === status ? 'white' : '#374151', 
                        border: 'none', 
                        borderRadius: '20px', 
                        cursor: 'pointer', 
                        fontWeight: '600', 
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                        flexShrink: 0
                      }}>
                      {status === 'new' && '🆕 Yeni'}
                      {status === 'preparing' && '👨‍🍳 Hazırlanıyor'}
                      {status === 'on_the_way' && '🚚 Yolda'}
                      {status === 'delivered' && '✅ Teslim'}
                      {status === 'cancelled' && '❌ İptal'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Müşteri & Telefon - Tek satırda tıklanabilir */}
              <div style={{ backgroundColor: '#f9fafb', padding: '14px', borderRadius: '12px', marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <p style={{ margin: '0', fontWeight: '700', fontSize: '16px' }}>{selectedOrder.customer_name}</p>
                    {selectedOrder.customer_email && <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '13px' }}>{selectedOrder.customer_email}</p>}
                  </div>
                  <a href={`tel:${selectedOrder.customer_phone}`} 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px', 
                      padding: '10px 16px', 
                      backgroundColor: '#22c55e', 
                      color: 'white', 
                      borderRadius: '25px', 
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '14px'
                    }}>
                    📞 Ara
                  </a>
                </div>
              </div>

              {/* Adres - Tıklanabilir */}
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedOrder.address_mahalle + ' ' + selectedOrder.address_sokak + ' ' + selectedOrder.address_bina_no + ' Maltepe İstanbul')}`}
                target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', backgroundColor: '#dbeafe', padding: '14px', borderRadius: '12px', marginBottom: '12px', textDecoration: 'none', color: 'inherit' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span style={{ fontSize: '20px' }}>📍</span>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0', fontWeight: '600', fontSize: '14px', color: '#1e40af' }}>Haritada Aç →</p>
                    <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#374151' }}>
                      {selectedOrder.address_mahalle}, {selectedOrder.address_sokak} No:{selectedOrder.address_bina_no}
                      {selectedOrder.address_kat_daire && ` (${selectedOrder.address_kat_daire})`}
                    </p>
                    {selectedOrder.address_tarifi && <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '13px', fontStyle: 'italic' }}>"{selectedOrder.address_tarifi}"</p>}
                  </div>
                </div>
              </a>

              {/* Sipariş Notu - Varsa */}
              {selectedOrder.note && (
                <div style={{ backgroundColor: '#fef3c7', padding: '12px 14px', borderRadius: '12px', marginBottom: '12px' }}>
                  <p style={{ margin: 0, fontSize: '14px' }}><strong>📝 Not:</strong> {selectedOrder.note}</p>
                </div>
              )}

              {/* Ürünler */}
              <div style={{ backgroundColor: '#f9fafb', padding: '14px', borderRadius: '12px', marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>🛒 Sipariş ({selectedOrder.items.length} ürün)</h4>
                {selectedOrder.items.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: idx < selectedOrder.items.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <span style={{ fontSize: '14px' }}><strong>{item.quantity}x</strong> {item.product_name}</span>
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>{item.total_price.toFixed(2)}₺</span>
                  </div>
                ))}
              </div>

              {/* Toplam - Büyük ve Net */}
              <div style={{ backgroundColor: '#fff7ed', padding: '16px', borderRadius: '12px', border: '2px solid #fed7aa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '14px' }}>
                  <span>Ara Toplam:</span>
                  <span>{selectedOrder.subtotal.toFixed(2)}₺</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', fontSize: '14px' }}>
                  <span>Teslimat:</span>
                  <span>{selectedOrder.delivery_fee === 0 ? 'ÜCRETSİZ' : `${selectedOrder.delivery_fee.toFixed(2)}₺`}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '10px', borderTop: '2px solid #fed7aa', fontSize: '22px', fontWeight: 'bold' }}>
                  <span>TOPLAM:</span>
                  <span style={{ color: '#d97706' }}>{selectedOrder.total.toFixed(2)}₺</span>
                </div>
                <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '8px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                  <span style={{ fontSize: '16px' }}>{selectedOrder.payment_method === 'cash' ? '💵' : selectedOrder.payment_method === 'card_on_delivery' ? '💳' : '🌐'}</span>
                  <span style={{ fontWeight: '600', fontSize: '14px' }}>{getPaymentMethod(selectedOrder.payment_method)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Paket Servis Kapatma Modal */}
      {showDeliveryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '450px', padding: '25px' }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#991b1b' }}>🔴 Paket Servisi Kapat</h2>
            <p style={{ color: '#6b7280', marginBottom: '20px' }}>
              Paket servis kapatıldığında müşteriler sipariş veremeyecek. Lütfen kapatma sebebini seçin veya yazın:
            </p>
            
            {/* Hazır Sebepler */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
              {['Pazar günü tatili', 'Bayram tatili', 'Kar yağışı', 'Teknik arıza', 'Personel eksikliği'].map(reason => (
                <button
                  key={reason}
                  onClick={() => setClosedReason(reason)}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: closedReason === reason ? '#fecaca' : '#f3f4f6',
                    color: closedReason === reason ? '#991b1b' : '#374151',
                    border: closedReason === reason ? '2px solid #ef4444' : '2px solid transparent',
                    borderRadius: '20px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {reason}
                </button>
              ))}
            </div>
            
            {/* Özel Sebep */}
            <input
              type="text"
              placeholder="Veya özel bir sebep yazın..."
              value={closedReason}
              onChange={(e) => setClosedReason(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '15px',
                marginBottom: '20px',
                boxSizing: 'border-box'
              }}
            />
            
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowDeliveryModal(false); setClosedReason(''); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                İptal
              </button>
              <button
                onClick={() => toggleDeliveryStatus(false, closedReason || 'Geçici olarak kapalı')}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                🔴 Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ürün Modal */}
      {showProductModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '500px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0 }}>{editingProduct ? '✏️ Ürün Düzenle' : '➕ Yeni Ürün'}</h2>
            </div>
            <form onSubmit={saveProduct} style={{ padding: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Ürün Adı *</label>
                <input type="text" value={productForm.name} onChange={(e) => setProductForm({...productForm, name: e.target.value})} required
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Kategori *</label>
                <select value={productForm.category_id} onChange={(e) => setProductForm({...productForm, category_id: e.target.value})} required
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}>
                  <option value="">Seçin</option>
                  {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Fiyat (₺) *</label>
                  <input type="number" step="0.01" value={productForm.price} onChange={(e) => setProductForm({...productForm, price: e.target.value})} required
                    style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Satış Birimi *</label>
                  <select value={productForm.sales_unit} onChange={(e) => setProductForm({...productForm, sales_unit: e.target.value})}
                    style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}>
                    <option value="adet">Adet (Tekli Satış)</option>
                    <option value="kg">Kilogram (Gram Seçimli)</option>
                  </select>
                </div>
              </div>
              {productForm.sales_unit === 'kg' && (
                <div style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px' }}>
                  <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}>
                    ⚖️ <strong>Kilogram satış:</strong> Girdiğiniz fiyat 1 kg için geçerlidir. Müşteri 250gr, 500gr, 750gr veya 1kg seçebilir.
                  </p>
                </div>
              )}
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Açıklama</label>
                <textarea value={productForm.description} onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', minHeight: '80px', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={productForm.is_available} onChange={(e) => setProductForm({...productForm, is_available: e.target.checked})} style={{ width: '20px', height: '20px' }} />
                  <span style={{ fontWeight: '600' }}>Stokta Mevcut</span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" onClick={() => setShowProductModal(false)} style={{ flex: 1, padding: '14px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>İptal</button>
                <button type="submit" disabled={loading} style={{ flex: 1, padding: '14px', backgroundColor: '#d97706', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>{loading ? 'Kaydediliyor...' : 'Kaydet'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Kategori Modal */}
      {showCategoryModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0 }}>➕ Yeni Kategori</h2>
            </div>
            <form onSubmit={saveCategory} style={{ padding: '20px' }}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Kategori Adı *</label>
                <input type="text" value={categoryForm.name} onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})} required
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600' }}>Açıklama</label>
                <textarea value={categoryForm.description} onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                  style={{ width: '100%', padding: '12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', minHeight: '60px', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="button" onClick={() => setShowCategoryModal(false)} style={{ flex: 1, padding: '14px', backgroundColor: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>İptal</button>
                <button type="submit" disabled={loading} style={{ flex: 1, padding: '14px', backgroundColor: '#d97706', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}>{loading ? 'Ekleniyor...' : 'Ekle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Yonetim;
